import {
  POSTHOG_TRACKING_EVENTS,
  type PosthogTrackingEventName,
} from './constants';

const PRODUCT_INSIGHT_EVENT_NAMES = {
  APPLICATION_LOADED: 'ApplicationLoaded',
  CONVERSATION_ACTIVITY: 'ConversationActivity',
  FEEDBACK_PROVIDED: 'FeedbackProvided',
  KNOWLEDGE_GRAPH_ACTIVITY: 'KnowledgeGraphActivity',
  MENU_CLICKED: 'MenuClicked',
  SHARED: 'Shared',
  SOURCE_CLICKED: 'SourceClicked',
  SUGGEST_SELECTED: 'SuggestSelected',
} as const;

type ProductInsightEventName =
  (typeof PRODUCT_INSIGHT_EVENT_NAMES)[keyof typeof PRODUCT_INSIGHT_EVENT_NAMES];

interface ProductInsightEventMetadata {
  constant: keyof typeof POSTHOG_TRACKING_EVENTS;
  eventName: ProductInsightEventName;
  tracker: string;
}

export const PRODUCT_INSIGHT_EVENT_METADATA = {
  [POSTHOG_TRACKING_EVENTS.CATALYST_DRAWER_OPEN]: {
    constant: 'CATALYST_DRAWER_OPEN',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.MENU_CLICKED,
    tracker: 'trackCatalystDrawerOpen',
  },
  [POSTHOG_TRACKING_EVENTS.EVENTS_DRAWER_OPEN]: {
    constant: 'EVENTS_DRAWER_OPEN',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.MENU_CLICKED,
    tracker: 'trackEventsDrawerOpen',
  },
  [POSTHOG_TRACKING_EVENTS.INSIGHTS_DRAWER_OPEN]: {
    constant: 'INSIGHTS_DRAWER_OPEN',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.CONVERSATION_ACTIVITY,
    tracker: 'trackInsightsDrawerOpen',
  },
  [POSTHOG_TRACKING_EVENTS.QUERY_SENT]: {
    constant: 'QUERY_SENT',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.CONVERSATION_ACTIVITY,
    tracker: 'trackAlfredQuerySent',
  },
  [POSTHOG_TRACKING_EVENTS.EARNINGS_INSIGHT_DRAWER_OPEN]: {
    constant: 'EARNINGS_INSIGHT_DRAWER_OPEN',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.MENU_CLICKED,
    tracker: 'trackEarningsInsightDrawerOpen',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_PYTHON_VIEW]: {
    constant: 'ALFRED_PYTHON_VIEW',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.CONVERSATION_ACTIVITY,
    tracker: 'trackAlfredPythonView',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_PROCESS_VIEW]: {
    constant: 'ALFRED_PROCESS_VIEW',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.CONVERSATION_ACTIVITY,
    tracker: 'trackAlfredProcessView',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_SHARE]: {
    constant: 'ALFRED_SHARE',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.SHARED,
    tracker: 'trackAlfredShare',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_CHATSEARCH_OPEN]: {
    constant: 'ALFRED_CHATSEARCH_OPEN',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.CONVERSATION_ACTIVITY,
    tracker: 'trackAlfredChatSearchOpen',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_CHART_ACTION]: {
    constant: 'ALFRED_CHART_ACTION',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.CONVERSATION_ACTIVITY,
    tracker: 'trackAlfredChartAction',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_FLOW_OPEN]: {
    constant: 'ALFRED_FLOW_OPEN',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.CONVERSATION_ACTIVITY,
    tracker: 'trackAlfredFlowOpen',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_PLANNING_VIEW]: {
    constant: 'ALFRED_PLANNING_VIEW',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.CONVERSATION_ACTIVITY,
    tracker: 'trackAlfredPlanningView',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_CHAT_BOOKMARK]: {
    constant: 'ALFRED_CHAT_BOOKMARK',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.CONVERSATION_ACTIVITY,
    tracker: 'trackAlfredChatBookmark',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_DEEP_SELECTED]: {
    constant: 'ALFRED_DEEP_SELECTED',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.CONVERSATION_ACTIVITY,
    tracker: 'trackAlfredDeepSelected',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_WEB_SEARCH_SELECTED]: {
    constant: 'ALFRED_WEB_SEARCH_SELECTED',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.CONVERSATION_ACTIVITY,
    tracker: 'trackAlfredWebSearchSelected',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_PREVIOUS_CONVERSATION_OPEN]: {
    constant: 'ALFRED_PREVIOUS_CONVERSATION_OPEN',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.CONVERSATION_ACTIVITY,
    tracker: 'trackAlfredPreviousConversationOpen',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_SOURCE_OPEN]: {
    constant: 'ALFRED_SOURCE_OPEN',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.SOURCE_CLICKED,
    tracker: 'trackAlfredSourceOpen',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_CHAT_COPY]: {
    constant: 'ALFRED_CHAT_COPY',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.CONVERSATION_ACTIVITY,
    tracker: 'trackAlfredChatCopy',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_FEEDBACK]: {
    constant: 'ALFRED_FEEDBACK',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.FEEDBACK_PROVIDED,
    tracker: 'trackAlfredFeedback',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_CONVERSATION_PRINT]: {
    constant: 'ALFRED_CONVERSATION_PRINT',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.CONVERSATION_ACTIVITY,
    tracker: 'trackAlfredConversationPrint',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_CONVERSATION_RENAME]: {
    constant: 'ALFRED_CONVERSATION_RENAME',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.CONVERSATION_ACTIVITY,
    tracker: 'trackAlfredConversationRename',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_CONVERSATION_STARTED]: {
    constant: 'ALFRED_CONVERSATION_STARTED',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.CONVERSATION_ACTIVITY,
    tracker: 'trackAlfredConversationStarted',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_TRUSTED_SOURCE_ADDED]: {
    constant: 'ALFRED_TRUSTED_SOURCE_ADDED',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.SOURCE_CLICKED,
    tracker: 'trackAlfredTrustedSourceAdded',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_TRUSTED_SOURCE_REMOVED]: {
    constant: 'ALFRED_TRUSTED_SOURCE_REMOVED',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.SOURCE_CLICKED,
    tracker: 'trackAlfredTrustedSourceRemoved',
  },
  [POSTHOG_TRACKING_EVENTS.ALFRED_OPENED]: {
    constant: 'ALFRED_OPENED',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.APPLICATION_LOADED,
    tracker: 'trackAlfredOpened',
  },
  [POSTHOG_TRACKING_EVENTS.PROMPT_REFINER_OPENED]: {
    constant: 'PROMPT_REFINER_OPENED',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.SUGGEST_SELECTED,
    tracker: 'trackPromptRefinerOpened',
  },
  [POSTHOG_TRACKING_EVENTS.PROMPT_REFINER_CLARIFICATION_ADDED]: {
    constant: 'PROMPT_REFINER_CLARIFICATION_ADDED',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.SUGGEST_SELECTED,
    tracker: 'trackPromptRefinerClarificationAdded',
  },
  [POSTHOG_TRACKING_EVENTS.PROMPT_REFINER_SUGGESTION_EDITED]: {
    constant: 'PROMPT_REFINER_SUGGESTION_EDITED',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.SUGGEST_SELECTED,
    tracker: 'trackPromptRefinerSuggestionEdited',
  },
  [POSTHOG_TRACKING_EVENTS.KG_BRAND_DRAWER_OPEN]: {
    constant: 'KG_BRAND_DRAWER_OPEN',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.KNOWLEDGE_GRAPH_ACTIVITY,
    tracker: 'trackKgBrandDrawerOpen',
  },
  [POSTHOG_TRACKING_EVENTS.KG_GEOGRAPHY_DRAWER_OPEN]: {
    constant: 'KG_GEOGRAPHY_DRAWER_OPEN',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.KNOWLEDGE_GRAPH_ACTIVITY,
    tracker: 'trackKgGeographyDrawerOpen',
  },
  [POSTHOG_TRACKING_EVENTS.THEME_OVERVIEW_DRAWER_OPEN]: {
    constant: 'THEME_OVERVIEW_DRAWER_OPEN',
    eventName: PRODUCT_INSIGHT_EVENT_NAMES.MENU_CLICKED,
    tracker: 'trackThemeOverviewDrawerOpen',
  },
} satisfies Record<PosthogTrackingEventName, ProductInsightEventMetadata>;
